import { createContext, useContext, useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";

// The live support chat. REACT_APP_BASEURLSOCKET is the socket.io server
// (the user service; dev: https://chat-dev.safrati.ai). Staff replies go only
// through it. When it is not set nothing connects, and the Support screen
// says replies cannot be sent instead of failing silently.
export const SOCKET_URL = process.env.REACT_APP_BASEURLSOCKET || "";

const SocketContext = createContext({
  socket: null,
  socketId: null,
  configured: false,
  connected: false,
});

export const SocketProvider = ({ children }) => {
  const [socketId, setSocketId] = useState(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);

  const dataUser = getLocalStorage(dataLocalStorage.userinfo);
  const token = dataUser?.token;

  useEffect(() => {
    if (!SOCKET_URL || !token) return undefined;
    // Only create a new socket if one doesn't exist
    if (!socketRef.current) {
      // The token goes in the handshake's auth data, never in the address
      // (where server and proxy logs would keep it).
      socketRef.current = io(SOCKET_URL, {
        auth: { token },
        transports: ["websocket"],
        secure: true,
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 2000,
      });

      socketRef.current.on("connect", () => {
        setSocketId(socketRef.current?.id || null);
        setConnected(true);
      });
      socketRef.current.on("disconnect", () => setConnected(false));
      socketRef.current.on("connect_error", () => setConnected(false));
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.close();
        socketRef.current = null;
        setSocketId(null);
        setConnected(false);
      }
    };
  }, [token]);

  return (
    <SocketContext.Provider
      value={{
        socket: socketRef.current,
        socketId,
        configured: !!SOCKET_URL,
        connected,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
